/**
 * MSW boot script.
 *
 *   • Called from `app/providers.tsx` once per browser tab.
 *   • Idempotent: a second call returns the same promise.
 *   • Server-rendered passes get `Promise.resolve()` immediately so SSR
 *     hydration doesn't block on a worker that only runs in the browser.
 *   • Mode resolution comes from `getApiMode()` — `real` skips MSW entirely.
 */

'use client';

import { getApiMode } from '@/lib/api';

let bootPromise: Promise<void> | null = null;

export const ensureMockReady = (): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  if (bootPromise === null) {
    bootPromise = bootMsw();
  }
  return bootPromise;
};

async function bootMsw(): Promise<void> {
  const mode = getApiMode();
  if (mode === 'real') {
    return;
  }

  try {
    const { setupWorker } = await import('msw/browser');
    const { getHandlers } = await import('./handlers');
    const worker = setupWorker(...getHandlers(mode));
    await worker.start({
      onUnhandledRequest: mode === 'mock' ? 'warn' : 'bypass',
      serviceWorker: { url: '/mockServiceWorker.js' },
      quiet: false,
    });
    console.info(`[msw] booted in '${mode}' mode`);
  } catch (err) {
    console.warn('[msw] failed to start; falling back to real network', err);
  }
}
